import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from './Button'
import { useOverlay } from './useOverlay'

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
}

/**
 * Right-hand slide-over. Dealer detail is deep enough that a centred dialog
 * would fight the table behind it; a panel keeps the list in view.
 */
export function Drawer({ open, onClose, title, subtitle, children }: DrawerProps): ReactNode {
  useOverlay(open, onClose)

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-40">
      <div
        className="absolute inset-0 animate-fade-in bg-black/65 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        data-overlay="drawer"
        className="absolute right-0 top-0 flex h-full w-full max-w-xl animate-slide-in-right flex-col border-l border-accent/30 bg-canvas shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-line bg-surface px-5 py-3.5">
          <div className="min-w-0">{title}</div>
          <div className="flex items-center gap-2">
            {subtitle}
            <Button variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close panel" />
          </div>
        </header>
        <div className="scroll-slim flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </aside>
    </div>,
    document.body
  )
}
